/**
 * Device capability detection — identifies mobile/touch devices for
 * adaptive UI and input handling.
 *
 * Strategy: UserAgent for OS-level classification (most reliable),
 * fallback to screen size + touch for tablets and unknown devices.
 */

/** Returns true if the current device should use mobile touch UI. */
export function isMobileDevice(): boolean {
  const ua = navigator.userAgent;

  // 1. UA says it's a mobile OS → definitely mobile
  if (/iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }

  // 2. UA says it's a desktop OS → definitely NOT mobile
  //    (even if it has a touchscreen — mouse/keyboard are primary)
  if (/Windows|Macintosh|Linux|CrOS/i.test(ua)) {
    return false;
  }

  // 3. Unknown UA (unusual browser, bot, etc.) — fallback to heuristics:
  //    small screen + no hover → probably a tablet or phone
  if (window.matchMedia('(hover: none)').matches) {
    if (window.innerWidth < 1024 || window.innerHeight < 1024) {
      return true;
    }
  }

  return false;
}

/** Debug helper — logs detection results. */
export function logDeviceInfo(): void {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const fine = window.matchMedia('(pointer: fine)').matches;
  const hoverHover = window.matchMedia('(hover: hover)').matches;
  const hoverNone = window.matchMedia('(hover: none)').matches;
  const touch = 'ontouchstart' in window;
  const points = navigator.maxTouchPoints || 0;
  const dpr = window.devicePixelRatio || 1;
  const isMobile = isMobileDevice();
  console.info(
    `[Device] isMobile=${isMobile} coarse=${coarse} fine=${fine} ` +
    `hover=${hoverHover} noHover=${hoverNone} ` +
    `touch=${touch} maxPoints=${points} dpr=${dpr} ` +
    `screen=${window.innerWidth}x${window.innerHeight} ` +
    `UA=${navigator.userAgent.substring(0, 80)}`,
  );
}
